import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Shield, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminLogin } from "@/lib/api";
import { toast } from "sonner";

function errText(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  return "Login failed. Check your details.";
}

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
    </svg>
  );
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async () => {
    if (!email || !password) {
      toast.error("Enter email and password.");
      return;
    }
    setLoading(true);
    try {
      const admin = await adminLogin(email.trim(), password);
      toast.success(`Welcome back, ${admin.name}`);
      navigate("/admin");
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setLoading(false);
    }
  };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="max-w-md mx-auto min-h-screen bg-slate-950 p-8 flex flex-col justify-center">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="inline-flex bg-purple-500 rounded-2xl p-3 mb-4 shadow-[0_0_25px_rgba(168,85,247,0.4)]">
          <Shield size={28} className="text-white" strokeWidth={2.5} />
        </div>
        <h1 className="font-display text-4xl font-black text-slate-50 leading-none">Admin Console</h1>
        <p className="text-slate-400 mt-2 mb-8">Manage partner venues, profiles & prizes.</p>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-2 block">Email</label>
            <Input
              data-testid="admin-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@partner.com"
              className="h-14 bg-slate-900 border-slate-700 rounded-2xl px-5 text-slate-50"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-[0.15em] text-slate-400 mb-2 block">Password</label>
            <Input
              data-testid="admin-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder="••••••••"
              className="h-14 bg-slate-900 border-slate-700 rounded-2xl px-5 text-slate-50"
            />
          </div>
        </div>

        <motion.button
          data-testid="admin-login-btn"
          whileTap={{ scale: 0.96 }}
          disabled={loading}
          onClick={submit}
          className="mt-8 w-full h-16 bg-purple-500 text-white rounded-2xl font-display font-black text-xl flex items-center justify-center gap-2 shadow-[0_0_25px_rgba(168,85,247,0.4)] disabled:opacity-70 no-tap-highlight"
        >
          {loading ? <Loader2 className="animate-spin" /> : "Sign In"}
        </motion.button>

        <div className="flex items-center gap-3 my-5">
          <div className="h-px flex-1 bg-slate-800" />
          <span className="text-xs font-bold uppercase tracking-wider text-slate-600">or</span>
          <div className="h-px flex-1 bg-slate-800" />
        </div>

        <motion.button
          data-testid="admin-google-btn"
          whileTap={{ scale: 0.96 }}
          onClick={googleSignIn}
          className="w-full h-14 bg-white text-slate-900 rounded-2xl font-bold text-base flex items-center justify-center gap-3 no-tap-highlight"
        >
          <GoogleIcon /> Continue with Google
        </motion.button>
        <p className="text-center text-slate-500 text-xs mt-3">Only the two authorised partner emails can sign in.</p>

        <button data-testid="admin-back-player-btn" onClick={() => navigate("/login")} className="mt-6 w-full text-slate-500 text-sm font-semibold">
          ← Back to player app
        </button>
      </motion.div>
    </div>
  );
}
